import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Send, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { fmtDate, fmtTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { UAvatar, Empty, PageHead, StatusBadge } from "@/components/common";

export function ChatList() {
  const { data } = useFetch("/chats", { live: (e) => e.type === "job" || e.type === "notification" });
  return (
    <div data-testid="chat-list-page">
      <PageHead title="Chat" sub="Percakapan per pekerjaan" />
      <div className="space-y-2">
        {!data ? <div className="h-16 bg-slate-200 rounded-2xl animate-pulse" /> : data.length === 0 ? <Empty title="Belum ada percakapan" desc="Chat aktif setelah worker ditugaskan." /> : data.map((c) => (
          <Link key={c.job_id} to={`/chat/${c.job_id}`} data-testid={`chat-item-${c.job_id}`} className="flex items-center gap-3 bg-white rounded-2xl border p-3 hover:shadow-md transition-shadow">
            <UAvatar path={c.partner_avatar} name={c.partner_name} className="w-12 h-12" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2"><p className="font-bold truncate">{c.partner_name}</p><span className="text-[10px] text-slate-400 shrink-0">{fmtTime(c.updated_at)}</span></div>
              <p className="text-[11px] text-emerald-700 font-semibold truncate">{c.title} · {c.as_worker ? "sebagai Worker" : "sebagai Pengguna"}</p>
              <p className="text-xs text-slate-500 truncate flex items-center gap-1">{c.locked && <Lock className="w-3 h-3" />}{c.last_message?.text || "Mulai percakapan"}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ChatRoom() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data, reload } = useFetch(`/jobs/${jobId}/messages`, { poll: 30000, live: (e) => e.type === "job" && e.job_id === jobId });
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/jobs/${jobId}/messages`, { text });
      setText("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  if (!data) return <div className="h-64 bg-slate-200 rounded-2xl animate-pulse" />;
  return (
    <div data-testid="chat-container" className="flex flex-col min-h-[calc(100vh-10rem)]">
      <div className="flex items-center gap-2 mb-3">
        <button data-testid="back-btn" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border grid place-items-center"><ArrowLeft className="w-4 h-4" /></button>
        <div className="min-w-0 flex-1"><Link to={`/jobs/${jobId}`} className="font-display font-bold truncate block">{data.title}</Link><StatusBadge status={data.status} /></div>
      </div>
      <div className="flex-1 space-y-2 pb-4">
        {data.messages.length === 0 && <p className="text-center text-xs text-slate-400 py-6">Belum ada pesan</p>}
        {data.messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-emerald-600 text-white rounded-br-md" : "bg-white border rounded-bl-md"}`}>
                {!mine && <p className="text-[10px] font-bold text-emerald-700">{m.sender_name}</p>}
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-slate-400"}`} title={fmtDate(m.created_at)}>{fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      {data.locked ? (
        <div data-testid="chat-locked-banner" className="sticky bottom-28 bg-slate-800 text-white text-sm rounded-2xl px-4 py-3 flex items-center gap-2"><Lock className="w-4 h-4" />Chat terkunci karena pekerjaan telah selesai. Riwayat tetap dapat dilihat.</div>
      ) : (
        <form onSubmit={send} className="sticky bottom-28 flex gap-2 bg-slate-50 pt-2">
          <input data-testid="chat-message-input" className="field h-12 rounded-full" placeholder="Tulis pesan..." value={text} onChange={(e) => setText(e.target.value)} />
          <button data-testid="chat-send-btn" className="w-12 h-12 shrink-0 rounded-full bg-emerald-600 text-white grid place-items-center hover:bg-emerald-700"><Send className="w-5 h-5" /></button>
        </form>
      )}
    </div>
  );
}
